import { TopicCategoryPicker } from './TopicCategoryPicker';
import React, { useId, useState } from 'react';
import { SchlagwortFeld } from './SchlagwortFeld';
import { PremiumFeld } from './PremiumFeld';
import { ModerationsHinweis } from './ModerationsHinweis';
import { useDarfModerieren } from '../../hooks/useDarfModerieren';
import { fälltAufEntwurf, MODERATIONS_TEXTE } from '../../utils/moderation';
import { XMarkIcon, PhotoIcon, DocumentTextIcon, FolderIcon, LinkIcon, MusicalNoteIcon } from '@heroicons/react/24/outline';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '../ui/MarkdownEditor.css';
import {
  ContentFormData,
  ContentMetadata,
  ContentType,
  HeadphonesRecommendation,
  HeadphonesLabels
} from '../../types';
import { BunnyFilePicker } from '../admin/BunnyFilePicker';

interface WebComponentFormProps {
  initialData?: Partial<ContentFormData & { metadata?: ContentMetadata }>;
  onSubmit: (data: ContentFormData) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  /**
   * Der Status, mit dem der Beitrag geöffnet wurde. Das Formular hat kein Statusfeld — es braucht den
   * Bestand nur, um VOR dem Speichern zu warnen, dass ein veröffentlichter Beitrag auf den Entwurf fällt
   * (F3, `fälltAufEntwurf`). Die Rückfrage beim Speichern stellt die Seite (`ContentManagement`).
   */
  bestandStatus?: string;
}

export const WebComponentForm: React.FC<WebComponentFormProps> = ({
  initialData = {},
  onSubmit,
  onCancel,
  isLoading = false,
  bestandStatus,
}) => {
  // Siehe `ui/Filterleiste.tsx`: erzeugte Wurzel statt fester Zeichenketten.
  const feldId = useId();
  // Die Bezahlschranke ändert nur die Moderation — Begründung in `utils/moderation.ts`.
  const darfModerieren = useDarfModerieren();
  // Ohne Statusfeld gibt es keine Auswahl: kein bewusstes Depublizieren, es gilt immer der Rückfall.
  const rückfall = fälltAufEntwurf(darfModerieren, bestandStatus, undefined);
  const rückfallId = `${feldId}-rueckfall-hinweis`;
  const [formData, setFormData] = useState<ContentFormData & { metadata?: ContentMetadata; language?: string; isPremium?: boolean }>({
    title: initialData.title || '',
    description: initialData.description || '',
    shortDescription: initialData.shortDescription || '',
    duration: initialData.duration || '',
    headphones: initialData.headphones || HeadphonesRecommendation.NO,
    keywords: initialData.keywords || '',
    // Dieses Formular ist ein Bearbeitungs-Fluss (ContentEditorFlow.WEB_COMPONENT), kein eigener
    // Inhaltstyp: gespeichert wird sein Ergebnis als BLOG — der Wert, den die Datenbank kennt.
    // Früher stand hier `ContentType.WEB_COMPONENT`, ein Wert, den das Prisma-Enum nie hatte;
    // ContentManagement bog ihn beim Absenden per Sonderfall auf 'BLOG' um.
    type: ContentType.BLOG,
    category: initialData.category || '',
    topicCategories: initialData.topicCategories || [],
    tags: initialData.tags || [],
    content: initialData.content || '',
    language: 'de',
    // Premium ueberall (Tom 2026-09-11). Vorher stand hier fest `false`: auch beim BEARBEITEN wurde
    // der mitgegebene Wert ignoriert, und ein Speichern stellte einen Premium-Artikel still frei.
    isPremium: initialData.isPremium ?? true,
    metadata: {
      subtitle: initialData.metadata?.subtitle || '',
      heroImage: initialData.metadata?.heroImage || '',
      teaserText: initialData.metadata?.teaserText || '',
      ...initialData.metadata
    }
  });

  const [heroImagePreview, setHeroImagePreview] = useState<string | null>(
    initialData.metadata?.heroImage || null
  );
  const [heroImageUrl, setHeroImageUrl] = useState<string>(
    initialData.metadata?.heroImage || ''
  );
  const [detailedContent, setDetailedContent] = useState(initialData.content || '');
  const [showBunnyPicker, setShowBunnyPicker] = useState(false);
  const [bunnyPickerType, setBunnyPickerType] = useState<'image' | 'video' | 'audio' | 'all'>('image');
  const [bunnyPickerContext, setBunnyPickerContext] = useState<'hero' | 'link'>('hero');

  // Eine Funktion, ein Job. Vorher hing beides an `handleInputChange` — Formularfeld ODER
  // metadata-Schlüssel, unterschieden über einen dritten Parameter. Damit ließ sich der
  // Wert nicht typisieren (`any`), weil er je nach Zweig etwas anderes bedeutete.
  const handleInputChange = <K extends keyof ContentFormData>(field: K, value: ContentFormData[K]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // metadata ist ein offener Sack (Index-Signatur) — hier ist `unknown` die ehrliche Angabe.
  const handleMetadataChange = (metadataKey: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [metadataKey]: value
      }
    }));
  };

  const handleHeroImageUrlChange = (url: string) => {
    setHeroImageUrl(url);
    if (url.trim()) {
      setHeroImagePreview(url);
      handleMetadataChange('heroImage', url);
    } else {
      setHeroImagePreview(null);
      handleMetadataChange('heroImage', '');
    }
  };

  const handleBunnyFileSelect = (url: string) => {
    if (bunnyPickerContext === 'hero') {
      setHeroImageUrl(url);
      setHeroImagePreview(url);
      handleMetadataChange('heroImage', url);
    } else if (bunnyPickerContext === 'link') {
      // Füge Link in den Markdown-Editor ein
      const fileName = url.split('/').pop() || 'Link';
      let markdownLink = '';
      
      if (bunnyPickerType === 'image') {
        // Für Bilder: ![alt](url)
        markdownLink = `![${fileName}](${url})`;
      } else if (bunnyPickerType === 'video') {
        // Für Videos: HTML-Video-Tag oder Link
        markdownLink = `<video src="${url}" controls></video>\n\n`;
      } else if (bunnyPickerType === 'audio') {
        // Für Audio: HTML-Audio-Tag oder Link
        markdownLink = `<audio src="${url}" controls></audio>\n\n`;
      } else {
        // Standard Link
        markdownLink = `[${fileName}](${url})`;
      }
      
      setDetailedContent(prev => prev + markdownLink);
    }
  };

  const openBunnyPicker = (type: 'image' | 'video' | 'audio' | 'all', context: 'hero' | 'link' = 'hero') => {
    setBunnyPickerType(type);
    setBunnyPickerContext(context);
    setShowBunnyPicker(true);
  };

  const removeHeroImage = () => {
    setHeroImagePreview(null);
    setHeroImageUrl('');
    handleMetadataChange('heroImage', '');
  };




  // Der Parameter ist ein Slug aus der Datenbank, kein Enum-Wert mehr — die Taxonomie ist zur
  // Laufzeit pflegbar, ein Compile-Zeit-Typ kann sie nicht mehr abbilden. Die Gültigkeit
  // sichert stattdessen die Auswahl-Komponente (nur bekannte oder bereits gesetzte Slugs).
  const handleSubTopicToggle = (subTopic: string) => {
    setFormData(prev => {
      const currentCategories = prev.topicCategories || [];
      const isSelected = currentCategories.includes(subTopic);
      
      return {
        ...prev,
        topicCategories: isSelected
          ? currentCategories.filter(c => c !== subTopic)
          : [...currentCategories, subTopic]
      };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      content: detailedContent
    });
  };

  return (
    <>
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basis-Informationen */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-secondary-900">Basis-Informationen</h3>
        
        <div>
          <label htmlFor={`${feldId}-titel`} className="block text-sm font-medium text-secondary-700 mb-2">
            Titel *
          </label>
          <input
            id={`${feldId}-titel`}
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            className="input w-full"
            placeholder="Titel des Blog Posts"
            required
          />
        </div>

        <div>
          <label htmlFor={`${feldId}-untertitel`} className="block text-sm font-medium text-secondary-700 mb-2">
            Untertitel (für Hero-Section)
          </label>
          <input
            id={`${feldId}-untertitel`}
            type="text"
            value={formData.metadata?.subtitle || ''}
            onChange={(e) => handleMetadataChange('subtitle', e.target.value)}
            className="input w-full"
            placeholder="Untertitel (wird in .hero-content-two angezeigt)"
          />
        </div>

        <div>
          <label htmlFor={`${feldId}-kurzbeschreibung`} className="block text-sm font-medium text-secondary-700 mb-2">
            Kurzbeschreibung *
          </label>
          <textarea
            id={`${feldId}-kurzbeschreibung`}
            value={formData.shortDescription || formData.description}
            onChange={(e) => handleInputChange('shortDescription', e.target.value)}
            className="input w-full"
            rows={3}
            placeholder="Kurzbeschreibung für Teaser und Übersicht"
            required
          />
        </div>

        {/* ── ZWEI Beschreibungen statt drei (Bereinigung 2026-09-01, Tom-Zielbild) ──────────
            ⚠️ Hier war die Verwechslung am teuersten: das Feld hiess „Beschreibung (Legacy)" mit
            dem Platzhalter „für Rückwärtskompatibilität" — und genau dieses Feld ist seit der
            Bereinigung DIE Beschreibung, die auf der Produktseite steht. Wer der alten
            Beschriftung folgte, schrieb seinen Text in die Lang-Box daneben und pflegte das
            angezeigte Feld gar nicht.
            Die Lang-Box ist weg, ihre Daten bleiben bis zum CONTRACT-Schnitt lesbar; dieses
            Formular befüllt sie nur nicht mehr neu. */}
        <div>
          <label htmlFor={`${feldId}-beschreibung`} className="block text-sm font-medium text-secondary-700 mb-2">
            Beschreibung
          </label>
          <textarea
            id={`${feldId}-beschreibung`}
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className="input w-full"
            rows={6}
            placeholder="Ausführliche Beschreibung — dieser Text steht auf der Produktseite."
          />
        </div>

        <div>
          <label htmlFor={`${feldId}-dauer`} className="block text-sm font-medium text-secondary-700 mb-2">
            Dauer
          </label>
          <input
            id={`${feldId}-dauer`}
            type="text"
            value={formData.duration || ''}
            onChange={(e) => handleInputChange('duration', e.target.value)}
            className="input w-full"
            placeholder="z.B. 30 Minuten, 1 Stunde, etc."
          />
        </div>

        <div>
          <label htmlFor={`${feldId}-kopfhoerer`} className="block text-sm font-medium text-secondary-700 mb-2">
            Kopfhörer
          </label>
          <select
            id={`${feldId}-kopfhoerer`}
            value={formData.headphones || HeadphonesRecommendation.NO}
            onChange={(e) => handleInputChange('headphones', e.target.value as HeadphonesRecommendation)}
            className="input w-full"
          >
            <option value={HeadphonesRecommendation.RECOMMENDED}>
              {HeadphonesLabels[HeadphonesRecommendation.RECOMMENDED]}
            </option>
            <option value={HeadphonesRecommendation.NOT_NECESSARY}>
              {HeadphonesLabels[HeadphonesRecommendation.NOT_NECESSARY]}
            </option>
            <option value={HeadphonesRecommendation.NO}>
              {HeadphonesLabels[HeadphonesRecommendation.NO]}
            </option>
          </select>
        </div>

        <div>
          <label htmlFor={`${feldId}-keywords`} className="block text-sm font-medium text-secondary-700 mb-2">
            Keywords
          </label>
          <input
            id={`${feldId}-keywords`}
            type="text"
            value={formData.keywords || ''}
            onChange={(e) => handleInputChange('keywords', e.target.value)}
            className="input w-full"
            placeholder="Keywords durch Komma getrennt..."
          />
          <p className="text-xs text-secondary-500 mt-1">
            Trennen Sie Keywords durch Kommas
          </p>
        </div>

        <div>
          <label htmlFor={`${feldId}-teaser`} className="block text-sm font-medium text-secondary-700 mb-2">
            Teaser-Text (für Right Sidebar)
          </label>
          <textarea
            id={`${feldId}-teaser`}
            value={formData.metadata?.teaserText || ''}
            onChange={(e) => handleMetadataChange('teaserText', e.target.value)}
            className="input w-full"
            rows={2}
            placeholder="Kurzer Teaser-Text für die Sidebar"
          />
          <p className="text-xs text-secondary-500 mt-1">
            Dieser Text wird in der Right-Sidebar als Teaser angezeigt
          </p>
        </div>
      </div>

      {/* Hero-Bild */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-secondary-900">Hero-Bild</h3>
        
        {/* URL-Eingabefeld für Bunny.net */}
        <div>
          {/* Ein Feld, nicht eine Gruppe: die Schaltfläche daneben öffnet nur den Datei-Wähler und
              schreibt in genau dieses Eingabefeld. */}
          <label htmlFor={`${feldId}-bildurl`} className="block text-sm font-medium text-secondary-700 mb-2">
            Bild-URL (Bunny.net CDN)
          </label>
          <div className="flex gap-2">
            <input
              id={`${feldId}-bildurl`}
              type="text"
              value={heroImageUrl}
              onChange={(e) => handleHeroImageUrlChange(e.target.value)}
              placeholder="https://ul-app.b-cdn.net/..."
              className="input flex-1"
            />
            <button
              type="button"
              onClick={() => openBunnyPicker('image', 'hero')}
              className="btn btn-outline"
              title="Von Bunny.net auswählen"
            >
              <FolderIcon className="w-4 h-4 mr-2" />
              Auswählen
            </button>
            {heroImagePreview && (
              <button
                type="button"
                onClick={removeHeroImage}
                className="btn btn-outline btn-error"
                title="Bild entfernen"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>
          {heroImagePreview && (
            <div className="mt-4 relative">
              <img
                src={heroImagePreview}
                alt="Hero preview"
                className="max-w-full h-auto rounded-lg max-h-64 border border-secondary-300"
              />
            </div>
          )}
          <p className="text-xs text-secondary-500 mt-1">
            Geben Sie die Bunny.net CDN-URL ein oder wählen Sie eine Datei aus
          </p>
          <p className="text-xs text-secondary-500 mt-1">
            Das Hero-Bild wird in .hero-content-one angezeigt (Hintergrundbild)
          </p>
        </div>
      </div>

      {/* Detaillierter Inhalt */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-secondary-900">Detaillierter Inhalt</h3>
        
        <div>
          {/* ⚠️ Kein `htmlFor`: `MDEditor` ist ein fremdes Bauteil und rendert eine Werkzeugleiste
              plus ein Schreibfeld, dessen `id` diese Datei nicht vergibt. Ein `htmlFor` müsste sie
              raten — und eine geratene Kennung zeigt ins Leere, ohne dass es jemand merkt.
              Deshalb die Gruppe. */}
          <span id={`${feldId}-hauptinhalt`} className="block text-sm font-medium text-secondary-700 mb-2">
            Hauptinhalt (für Main-Content-Bereich) *
          </span>
          <div role="group" aria-labelledby={`${feldId}-hauptinhalt`} data-color-mode="light" className="markdown-editor-wrapper">
            <MDEditor
              value={detailedContent}
              onChange={(value) => setDetailedContent(value || '')}
              preview="edit"
              hideToolbar={false}
              textareaProps={{
                placeholder: 'Schreiben Sie hier Ihren Inhalt... Sie können Markdown verwenden oder HTML eingeben.',
                style: {
                  fontSize: 14,
                  minHeight: '400px',
                },
              }}
              height={500}
              previewOptions={{
                rehypePlugins: [],
                remarkPlugins: [],
              }}
              data-color-mode="light"
            />
          </div>
          <div className="mt-2 flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-secondary-500">
              <DocumentTextIcon className="w-4 h-4" />
              <span>
                Markdown wird unterstützt. Sie können auch HTML verwenden.
              </span>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => openBunnyPicker('image', 'link')}
                className="btn btn-outline btn-xs"
                title="Bild-Link einfügen"
              >
                <PhotoIcon className="w-4 h-4 mr-1" />
                Bild
              </button>
              <button
                type="button"
                onClick={() => openBunnyPicker('video', 'link')}
                className="btn btn-outline btn-xs"
                title="Video-Link einfügen"
              >
                <LinkIcon className="w-4 h-4 mr-1" />
                Video
              </button>
              <button
                type="button"
                onClick={() => openBunnyPicker('audio', 'link')}
                className="btn btn-outline btn-xs"
                title="Audio-Link einfügen"
              >
                <MusicalNoteIcon className="w-4 h-4 mr-1" />
                Audio
              </button>
            </div>
          </div>
          <p className="text-xs text-secondary-500 mt-2">
            <strong>Tipp:</strong> Verwenden Sie Markdown für bessere Formatierung:
            <br />
            • <code>**fett**</code> für <strong>fett</strong>
            <br />
            • <code>*kursiv*</code> für <em>kursiv</em>
            <br />
            • <code># Überschrift</code> für Überschriften
            <br />
            • <code>- Liste</code> für Aufzählungen
            <br />
            • <code>[Link](url)</code> für Links
            <br />
            • <code>![Bild](url)</code> für Bilder
          </p>
        </div>
      </div>

      {/* Themen-Kategorien */}
      <div>
        {/* ⚠️ Kein `htmlFor`: darunter steht eine Liste von Kontrollkästchen, kein einzelnes Feld
            — jedes Kästchen trägt seine eigene, umschließende Beschriftung. */}
        <span id={`${feldId}-themen`} className="block text-sm font-medium text-secondary-700 mb-2">
          Themen-Kategorien
        </span>
        <div role="group" aria-labelledby={`${feldId}-themen`}>
          <TopicCategoryPicker
            selected={formData.topicCategories || []}
            onToggle={handleSubTopicToggle}
          />
        </div>
        <p className="text-xs text-secondary-500 mt-2">
          Klicken Sie auf ein Hauptthema, um die Subthemen anzuzeigen. Wählen Sie eine oder mehrere Subthemen aus.
        </p>
      </div>

      {/* Kategorie & Tags */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${feldId}-kategorie`} className="block text-sm font-medium text-secondary-700 mb-2">
            Kategorie
          </label>
          <input
            id={`${feldId}-kategorie`}
            type="text"
            value={formData.category || ''}
            onChange={(e) => handleInputChange('category', e.target.value)}
            className="input w-full"
            placeholder="z.B. Neuheiten, Anleitung, etc."
          />
        </div>

        {/* Tags — vierte und letzte Flaeche, die die Bedienung von Hand schrieb.
            ⚠️ Sie war NICHT zeichengleich mit den drei anderen: `gap-1 px-2 py-1`,
            `bg-secondary-100 text-secondary-700` und `rounded` statt `rounded-full` — dieselbe
            Sache in anderer Anmutung, auf einer Redaktions-Flaeche. Beim Heben von
            `SchlagwortFeld` (2026-09-08) wurde sie deshalb bewusst AUSGELASSEN und gemeldet
            statt still mitgenommen; angeglichen wird sie jetzt auf Toms Entscheid
            („Ich folge bei allen neun Punkten deinen Empfehlungen", 2026-09-08).
            ⚠️ Die Pille sieht danach anders aus als vorher. Das ist der Zweck, nicht ein
            Nebeneffekt: vier Flaechen, ein Zeichen. */}
        <SchlagwortFeld
          werte={formData.tags || []}
          onÄndern={(tags) => setFormData((prev) => ({ ...prev, tags }))}
        />
      </div>

      {/* Content-Einstellungen */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-secondary-900">Content-Einstellungen</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor={`${feldId}-sprache`} className="block text-sm font-medium text-secondary-700 mb-2">
              Sprache
            </label>
            <select
              id={`${feldId}-sprache`}
              value={formData.language || 'de'}
              onChange={(e) => handleInputChange('language', e.target.value)}
              className="input w-full"
            >
              <option value="de">Deutsch</option>
              <option value="en">English</option>
              <option value="fr">Français</option>
              <option value="es">Español</option>
            </select>
          </div>

          <PremiumFeld
            wert={formData.isPremium ?? true}
            onÄndern={(wert) => handleInputChange('isPremium', wert)}
            gesperrt={!darfModerieren}
          >
            Premium an = nur für Premium-Abonnenten; aus = Free (für alle)
          </PremiumFeld>
        </div>
      </div>

      {/* Audio, Video und MindBook warnen am Statusfeld; dieses Formular hat keins, und die Rückfrage
          allein käme erst nach getaner Arbeit (BACKLOG `cms-blog-rueckfall-erst-in-der-rueckfrage`).
          Deshalb steht die Warnung über der Knopfzeile und beschreibt den Speichern-Knopf. */}
      {rückfall && (
        <ModerationsHinweis id={rückfallId} warnung>
          {MODERATIONS_TEXTE.rückfallHinweisBeitrag}
        </ModerationsHinweis>
      )}

      {/* Form Actions */}
      <div className="flex justify-end gap-4 pt-4 border-t">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-outline"
            disabled={isLoading}
          >
            Abbrechen
          </button>
        )}
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isLoading}
          aria-describedby={rückfall ? rückfallId : undefined}
        >
          {isLoading ? 'Wird gespeichert...' : 'Blog Post speichern'}
        </button>
      </div>

    </form>
    
    {/* Bunny.net File Picker - außerhalb des Formulars */}
    <BunnyFilePicker
      isOpen={showBunnyPicker}
      onClose={() => setShowBunnyPicker(false)}
      onSelect={handleBunnyFileSelect}
      fileType={bunnyPickerType}
      allowUrlInput={true}
      title={
        bunnyPickerContext === 'hero'
          ? 'Hero-Bild von Bunny.net auswählen'
          : bunnyPickerType === 'image'
          ? 'Bild-Link von Bunny.net auswählen'
          : bunnyPickerType === 'video'
          ? 'Video-Link von Bunny.net auswählen'
          : 'Audio-Link von Bunny.net auswählen'
      }
    />
    </>
  );
};

